import type { StaffingLine } from '@/api/processStepsApi'

/**
 * Pure logic for the Process → Staffing tab.
 *
 * Split out of the `.tsx` deliberately: this repo's vitest config is
 * `environment: 'node'` with `include: ['src/**\/*.test.ts']`, so anything left
 * in a component is untestable here. Parsing, the person-hours total and the
 * save-payload shape are exactly the parts worth pinning.
 */

/**
 * Starter qualifications for the role dropdown.
 *
 * A STOPGAP, and deliberately a short one. The staffing master-data model (P5a)
 * does not exist yet, so there is nothing to read a catalog from — but a free
 * text box invites "CT Tech", "Cell Therapy Tech" and "cell therapy technician"
 * to become three roles that scheduling cannot reconcile. A curated list makes
 * the common cases one click and consistent.
 *
 * NOT the RBAC roles. Those (Administrator, Supervisor, User) say who may click
 * what in the app; these say who is qualified to run the step. Wiring the two
 * together would be a category error that only shows up when someone staffs a
 * cleanroom from a permissions list.
 *
 * Free text stays available alongside it — every site names its qualifications
 * differently, and locking authoring to this list before a real catalog exists
 * would block work rather than tidy it.
 */
export const STAFFING_ROLE_SUGGESTIONS = [
  'Cell Therapy Technician',
  'Manufacturing Associate',
  'Senior Manufacturing Associate',
  'Apheresis Nurse',
  'Cleanroom Operator',
  'Materials Handler',
  'Process Engineer',
  'QC Analyst',
  'QA Specialist',
  'Manufacturing Supervisor',
] as const

/**
 * Whether a role was typed rather than picked.
 *
 * Compared case-insensitively and trimmed: a row loaded from the database that
 * says "cell therapy technician" is the listed role, and forcing it into the
 * free-text branch would make an unedited row look edited.
 */
export function isCustomRole(roleLabel: string): boolean {
  const value = roleLabel.trim().toLowerCase()
  if (!value) return false
  return !STAFFING_ROLE_SUGGESTIONS.some((r) => r.toLowerCase() === value)
}

/** The suggestion matching `roleLabel`, or null when it is free text. */
export function matchSuggestion(roleLabel: string): string | null {
  const value = roleLabel.trim().toLowerCase()
  return STAFFING_ROLE_SUGGESTIONS.find((r) => r.toLowerCase() === value) ?? null
}

/** The seven day keys, Monday first — the order the picker renders. */
export const SCHEDULE_DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const
export type ScheduleDay = (typeof SCHEDULE_DAYS)[number]

const DAY_SET = new Set<string>(SCHEDULE_DAYS)

/**
 * One staffing line being edited.
 *
 * Hours and counts are held as STRINGS. A controlled numeric input bound to a
 * number fights partial entry — typing "0." or clearing the box to retype
 * either snaps back or becomes NaN mid-keystroke. Same reason
 * `EquipmentRequirementTab` holds its draft rows this way.
 */
export interface StaffingDraft {
  staffingRoleId: string | null
  roleLabel: string
  prepHrs: string
  setupHrs: string
  runHrs: string
  unloadHrs: string
  numResources: string
  scheduleDays: ScheduleDay[]
}

/** A blank line, ready to type into. */
export function emptyStaffingDraft(): StaffingDraft {
  return {
    staffingRoleId: null,
    roleLabel: '',
    prepHrs: '0',
    setupHrs: '0',
    runHrs: '0',
    unloadHrs: '0',
    numResources: '1',
    scheduleDays: [],
  }
}

/** Fractional hours; NaN and negatives collapse to 0. */
export function hrs(value: string): number {
  const n = Number(value)
  return Number.isFinite(n) && n >= 0 ? n : 0
}

/** Head-count; integer ≥ 1, anything else collapses to 1. */
export function resources(value: string): number {
  const n = Math.floor(Number(value))
  return Number.isFinite(n) && n >= 1 ? n : 1
}

/**
 * Keep only recognised day keys, in canonical Mon–Sun order.
 *
 * `scheduleDays` is a JSON column, so a row can carry anything a previous
 * version or a hand-edit put there. Normalising on the way in means the picker
 * renders a consistent order and never a stray key it has no toggle for.
 */
export function normalizeDays(value: unknown): ScheduleDay[] {
  if (!Array.isArray(value)) return []
  const present = new Set(
    value.filter((d): d is string => typeof d === 'string').map((d) => d.toLowerCase()),
  )
  return SCHEDULE_DAYS.filter((d) => present.has(d) && DAY_SET.has(d))
}

/** Seed an editable draft from a persisted line. */
export function toStaffingDraft(line: StaffingLine): StaffingDraft {
  return {
    staffingRoleId: line.staffingRoleId,
    roleLabel: line.roleLabel ?? '',
    prepHrs: String(line.prepHrs),
    setupHrs: String(line.setupHrs),
    runHrs: String(line.runHrs),
    unloadHrs: String(line.unloadHrs),
    numResources: String(line.numResources),
    scheduleDays: normalizeDays(line.scheduleDays),
  }
}

/**
 * Person-hours for one line: every phase, multiplied by head-count.
 *
 * The slide is explicit that person-time is NOT equipment-time — two operators
 * on a one-hour run is two person-hours — which is why `numResources` multiplies
 * rather than sitting alongside as a separate figure.
 */
export function personHoursFor(draft: StaffingDraft): number {
  const perPerson =
    hrs(draft.prepHrs) + hrs(draft.setupHrs) + hrs(draft.runHrs) + hrs(draft.unloadHrs)
  return perPerson * resources(draft.numResources)
}

/** Person-hours per run across every line — mirrors the summary bar's figure. */
export function totalPersonHours(drafts: readonly StaffingDraft[]): number {
  // Rounded to 2dp: fractional hours summed as floats otherwise surface
  // 3.0000000000000004 in a header that is meant to be read at a glance.
  const total = drafts.reduce((sum, d) => sum + personHoursFor(d), 0)
  return Math.round(total * 100) / 100
}

/**
 * A line nobody can schedule.
 *
 * A role with no name cannot be staffed, and a line that books no time cannot be
 * planned against. Both are caught here so the answer arrives while the author
 * is still looking at the row.
 */
export function isIncomplete(draft: StaffingDraft): boolean {
  if (!draft.roleLabel.trim()) return true
  return personHoursFor(draft) <= 0
}

/** The staffing half of the upsert payload, in canonical order. */
export function toStaffingPayload(drafts: readonly StaffingDraft[]) {
  return drafts.map((d, i) => ({
    staffingRoleId: d.staffingRoleId ?? undefined,
    roleLabel: d.roleLabel.trim() || undefined,
    prepHrs: hrs(d.prepHrs),
    setupHrs: hrs(d.setupHrs),
    runHrs: hrs(d.runHrs),
    unloadHrs: hrs(d.unloadHrs),
    numResources: resources(d.numResources),
    scheduleDays: d.scheduleDays.length > 0 ? d.scheduleDays : undefined,
    order: i,
  }))
}

/** Stable comparison key for the dirty check. */
export function serializeStaffing(drafts: readonly StaffingDraft[]): string {
  return JSON.stringify(toStaffingPayload(drafts))
}
